import { createReducer, on, Action } from '@ngrx/store';
import { EntityState, EntityAdapter, createEntityAdapter } from '@ngrx/entity';

import * as LabResultsActions from './lab-results.actions';
import { LabResultEntity } from './lab-results.models';

export const LAB_RESULTS_FEATURE_KEY = 'labResults';

export interface State extends EntityState<LabResultEntity> {
  selectedId?: string | number;
  loading: boolean;
  saving: boolean;
  creating: boolean;
  error?: string | null;
  limit: number;
  offset: number;
}

export interface LabResultsPartialState {
  readonly [LAB_RESULTS_FEATURE_KEY]: State;
}

export const labResultsAdapter: EntityAdapter<LabResultEntity> =
  createEntityAdapter<LabResultEntity>({
    selectId: (labResult: LabResultEntity) => labResult._id,
    sortComparer: (a: LabResultEntity, b: LabResultEntity) => {
      // Sort by most recent first (descending order)
      const dateA = new Date(a.collectedAt || a.createdAt || 0).getTime();
      const dateB = new Date(b.collectedAt || b.createdAt || 0).getTime();
      return dateB - dateA; // Descending order (newest first)
    }
  });

export const initialState: State = labResultsAdapter.getInitialState({
  // set initial required properties
  selectedId: undefined,
  loading: false,
  saving: false,
  creating: false,
  error: null,
  limit: 100,
  offset: 0
});

const labResultsReducer = createReducer(
  initialState,
  on(LabResultsActions.loadLabResults, state => ({
    ...state,
    loading: true,
    error: null
  })),
  on(LabResultsActions.loadLabResultsSuccess, (state, { labResults }) =>
    labResultsAdapter.setAll(labResults, {
      ...state,
      loading: false
    })
  ),
  on(LabResultsActions.loadLabResultsFailure, (state, { error }) => ({
    ...state,
    error,
    loading: false
  })),

  on(LabResultsActions.updateLabResult, state => ({
    ...state,
    saving: true,
    error: null
  })),
  on(LabResultsActions.updateLabResultSuccess, (state, { labResult }) =>
    labResultsAdapter.setOne(labResult, { ...state, saving: false })
  ),
  on(LabResultsActions.updateLabResultFailure, (state, { error }) => ({
    ...state,
    saving: false,
    error
  })),
  on(LabResultsActions.setActiveLabResultId, (state, { labResultId }) => ({
    ...state,
    selectedId: labResultId
  })),

  on(LabResultsActions.loadAllLabResults, state => ({
    ...state,
    loading: true,
    error: null
  })),
  on(
    LabResultsActions.loadAllLabResultsSuccess,
    (state, { loadType, labResults, limit, offset }) => {
      return loadType === 'older'
        ? labResultsAdapter.upsertMany(labResults, {
            ...state,
            loading: false,
            limit,
            offset
          })
        : labResultsAdapter.setAll(labResults, {
            ...state,
            loading: false,
            limit,
            offset
          });
    }
  ),
  on(LabResultsActions.loadAllLabResultsFailure, (state, { error }) => ({
    ...state,
    error,
    loading: false
  })),

  on(LabResultsActions.createLabResult, state => ({
    ...state,
    creating: true,
    error: null
  })),
  on(LabResultsActions.createLabResultSuccess, (state, { labResult }) =>
    labResultsAdapter.addOne(labResult, { ...state, creating: false })
  ),
  on(LabResultsActions.createLabResultFailure, (state, { error }) => ({
    ...state,
    creating: false,
    error
  })),
  on(LabResultsActions.submitLabResultPdf, state => ({
    ...state,
    creating: true,
    error: null
  })),
  on(LabResultsActions.submitLabResultPdfSuccess, state => ({
    ...state,
    creating: false
  })),
  on(LabResultsActions.submitLabResultPdfFailure, (state, { error }) => ({
    ...state,
    creating: false,
    error
  }))
);

export function reducer(state: State | undefined, action: Action) {
  return labResultsReducer(state, action);
}
